<template>
  <div class="container import">
    <div class="import__title-container">
      <div class="import__title-left">
        <div class="import__header-button-wrapper"
             v-if="isDataFetched">
          <button
            @click.stop="cancelSortingAndFilters"
            class="import__clear-filters">
            <span>clear all filters and sorting</span>
            <span class="import__cross">&times;</span>
          </button>
        </div>

      </div>
      <div class="import__title-right"
           v-if="isDataFetched">
        <span v-if="importCreated">Note: New imports will take several minutes to process. Refresh the page to see updated status.</span>
        <div class="import__imports">
          <span class="import__count">Imports: {{totalItems}}</span>
          <base-select
            class="import__grid-select"
            :options="paginationOptions"
            alignLeft
            :titleStyle="{'padding-left': '5px'}"
            :headerStyle="{}"
            showTopBorder
            shiftLabelRight
            v-model="pageItemsLimit"
            :selectStyle="{'max-height': '35px !important', width: '100px !important'}"
            @change="handleLimitChange"
          />
        </div>
        <button class="import__button" @click="openCreateModal()">
          Create Import
        </button>
      </div>
    </div>

    <div class="common-table import__table" :class="{'import__table_collapsed': !isNavbarCollapsed}">
      <div class="common-table__row common-table__header"
           v-if="isDataFetched">
        <div class="common-table__header-cell"
             v-for="column in importTableColumnsConfig"
             :key="column.property"
             :class="{[`import__${column.property}`]: true}">
          <base-multi-select-sortable-header
            v-if="column.property !== 'download-button'"
            class="import__select-header"
            :column="column"
            :sort-config="sortConfig"
            :predefinedOptions="filterParams[column.property]"
            :predefinedNumberFilter="['id'].includes(column.property) ? filterParams[column.property] : null"
            :predefinedDateRangeFilter="['timestamp_processed', 'timestamp_failed', 'timestamp_completed'].includes(column.property) ? {start: filterParams[column.property]['date_from'], end: filterParams[column.property]['date_to']}: null"
            @sort="onHeaderCellClicked(column, $event)"
            @apply="filterColumn($event, column)"
            @cancel="cancelFilter($event, column)"
            :ref="sortableHeaderRefs[column.property]"
          />
        </div>
      </div>
      <template v-if="dataMissing">
        <div class="common-table__row no-data-block">
          No records found
        </div>
      </template>
      <div class="" v-else-if="!isDataFetched">
        <div class="import__loader">
          <base-loader/>
        </div>
      </div>
      <template v-else>
        <template v-for="(record) in paginatedKeywords">
          <div :key="record.id" class="common-table__row">
            <div class="common-table__cell"
                 v-for="column in importTableColumnsConfig"
                 :key="column.property"
                 :class="[`import__${column.property}`, `import__row_${column.property}`]">
              <template v-if="column.property === 'download-button'">
                <a @click="downloadFile(record.id)" class="download-icon" target="_blank"
                   v-tooltip.bottom-start="{content: 'Click to download', ...tooltipOptions}">
                  <base-material-design-icon icon="fileDownload"/>
                </a>
              </template>
              <template v-else-if="column.property === 'name'">
                  <span class="import__link"
                        v-tooltip.bottom-start="{content: 'Click to review', ...tooltipOptions}"
                        @click="openReviewModal(record.id)">
                    {{ record[column.property] }}
                  </span>
              </template>
              <template v-else>
                  <span>
                    {{ record[column.property] }}
                  </span>
              </template>
            </div>
          </div>
        </template>
      </template>
    </div>
    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="import__update-loader" v-if="isDataUpdating">
      <base-loader class="import__update-loader-ring"/>
    </div>

    <modal name="create"
           v-bind="changeImportModalProps">
      <CreateImportPopup
        ref="create-import"
        @apply="createImport($event)"
        @cancel="closeCreateModal"
      />
    </modal>

    <modal name="review"
           v-bind="changeImportModalProps">
      <ReviewImportPopup
        ref="review-import"
        :review="importReview"
        @cancel="closeReviewModal"
        @apply="reviewApplyHandler"
      />
    </modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import CreateImportPopup from './CreateImportPopup'
import ReviewImportPopup from './ReviewImportPopup'
import _ from 'lodash'
import BaseSortMixins from '@/common/BaseSortMixins'

const ITEMS_PER_PAGE = 100
const PAGINATION_OPTIONS = [
  {
    label: '100',
    value: 100
  },
  {
    label: '250',
    value: 250
  },
  {
    label: '500',
    value: 500
  }
]
const EMPTY_FILTERS = {
  id: {
    operator: null,
    value: null
  },
  name: null,
  type: [],
  timestamp_processed: {
    date_from: null,
    date_to: null
  },
  timestamp_failed: {
    date_from: null,
    date_to: null
  },
  timestamp_completed: {
    date_from: null,
    date_to: null
  },
  sort_column: [],
  sort_order: [],
  page_from: 0,
  page_to: ITEMS_PER_PAGE
}

// table config
const DEFAULT_CONFIG = [
  {
    label: '',
    property: 'download-button'
  },
  {
    label: 'ID',
    property: 'id',
    isNumber: true
  },
  {
    label: 'Type',
    property: 'type',
    options: [
      {
        label: 'Rank Report',
        value: 'rank_report'
      },
      {
        label: 'Keywords',
        value: 'client'
      },
      {
        label: 'URLs',
        value: 'url'
      }
    ]
  },
  {
    label: 'Name',
    property: 'name',
    options: []
  },
  {
    label: 'Processed',
    property: 'timestamp_processed'
  },
  {
    label: 'Failed',
    property: 'timestamp_failed'
  },
  {
    label: 'Completed',
    property: 'timestamp_completed'
  }
]

export default {
  name: 'Importer',
  components: { CreateImportPopup, ReviewImportPopup },
  mixins: [BaseSortMixins],
  data () {
    return {
      isDataFetched: false,
      importReview: null,
      isDataUpdating: false,
      sortConfig: [],
      pageItemsLimit: ITEMS_PER_PAGE,
      importTableColumnsConfig: _.cloneDeep(DEFAULT_CONFIG),
      filterParams: _.cloneDeep(EMPTY_FILTERS),
      tooltipOptions: {
        delay: { show: 100, hide: 100 },
        classes: 'rfx-tooltip'
      },
      sortableHeaderRefs: {
        'id': 'id',
        'name': 'name',
        'timestamp_processed': 'timestamp_processed',
        'timestamp_failed': 'timestamp_failed',
        'timestamp_completed': 'timestamp_completed'
      },
      paginationOptions: [...PAGINATION_OPTIONS],
      importCreated: false
    }
  },
  watch: {
    clientId: {
      handler (v) {
        v && this.loadFilteredData()
      },
      immediate: true
    }
  },
  computed: {
    ...mapState(['isNavbarCollapsed']),
    ...mapState('importer', ['imports']),
    dataMissing () {
      return this.isDataFetched && !this.imports.length
    },
    changeImportModalProps () {
      const changeImportModalProps = {
        width: 690
      }
      return { ...baseModalProps, ...changeImportModalProps }
    },
    hasPagination () {
      return (this.isDataFetched && !this.isDataUpdating) && this.totalItems > this.pageItemsLimit
    },
    totalItems () {
      return this.imports.length
    },
    paginatedKeywords () {
      let imports = _.cloneDeep(this.imports)
      const { page_from = 0, page_to = 100 } = this.filterParams
      return imports.slice(page_from, page_to)
    }
  },
  methods: {
    ...mapActions('importer', ['getImportData', 'createNewImport', 'downloadImportedFile', 'getImportReview']),
    async loadFilteredData () {
      this.isDataFetched && (this.isDataUpdating = true)
      await this.getImportData({
        clientId: this.clientId,
        filters: this.filterParams
      })
      this.isDataFetched = true
      this.isDataUpdating = false
    },
    async downloadFile (id) {
      this.isDataUpdating = true
      const downloadComplete = await this.downloadImportedFile({ id })
      if (downloadComplete) {
        this.isDataUpdating = false
      }
    },
    reset () {
      this.sortConfig = []
      this.importTableColumnsConfig = DEFAULT_CONFIG
      this.loadPageData()
    },
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    },
    async createImport (record) {
      try {
        await this.createNewImport({ clientId: this.clientId, record })
        await this.loadFilteredData()
        this.importCreated = true
        this.closeCreateModal()
      } catch (e) {
        this.$refs['create-import'].showErrors(e[0])
      } finally {
        this.isDataUpdating = false
      }
    },
    closeCreateModal () {
      this.$modal.hide('create')
    },
    closeReviewModal () {
      this.importReview = null
      this.$modal.hide('review')
    },
    reviewApplyHandler () {
      this.closeReviewModal()
      this.openCreateModal()
    },
    openCreateModal () {
      this.$modal.show('create')
    },
    async openReviewModal (importId) {
      this.isDataUpdating = true
      this.importReview = await this.getImportReview(importId)
      if (this.importReview) {
        this.importReview.id = importId
        this.isDataUpdating = false
        this.$modal.show('review')
      }
    },
    handleLimitChange (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
    },
    filterColumn (event, column) {
      this.filterParams[column.property] = event
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
      this.loadFilteredData()
    },
    onHeaderCellClicked (cell, direction, declineCall) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property
        }]
      }
      this.filterParams = {
        ...this.filterParams,
        sort_column: this.sortConfig.map(item => item.propertyName),
        sort_order: this.sortConfig.map(item => item.sortDirection)
      }
      !declineCall && this.loadFilteredData()
    },
    cancelSortingAndFilters () {
      const emptyFilters = _.cloneDeep(EMPTY_FILTERS)
      this.filterParams = { ...this.filterParams, ...emptyFilters, page_to: this.pageItemsLimit }
      Object.keys(this.sortableHeaderRefs)
        .forEach(ref => {
          const [element] = this.$refs[ref]
          element.$refs[element.childRef].cancel(true)
        })
      this.sortConfig = []
      !this.isDataUpdating && this.loadFilteredData()
    },
    cancelFilter (event, column) {
      this.filterParams[column.property] = null
      this.loadFilteredData()
    }
  }
}
</script>

<style scoped lang="scss">

.common-table {
  border-radius: $rfx-default-border-radius;
  overflow: visible;

  &__header {
    background-color: rgba(1, 22, 39, 0.1);
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
  }

  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
    padding: 0;
    height: 60px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__cell {
    color: $rfx-dark-march;
    height: 60px;
    padding: 0;
    display: flex;
    align-items: center;
    font-size: 14px;
    border-bottom: 1px solid $rfx-light-steel;

    &:first-child {
      padding-left: 25px;
    }

    &:last-child {
      padding-right: 25px;
    }
  }

  &__row {
    border-left: 1px solid $rfx-light-steel;
    border-right: 1px solid $rfx-light-steel;
  }
}

.import {
  position: relative;
  min-height: 200vh !important;

  &__cross {
    font-size: 21px;
    margin-left: 5px;
    margin-top: -2px;
  }

  &__title-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 50%;
    height: 60px;
  }

  &__title-right > *:not(:last-child) {
    margin-right: 30px;
  }

  &__title-left {
    display: flex;
    align-items: center;
  }

  &__title-left > *:not(:last-child) {
    margin-right: 30px;
  }

  &__header-button-wrapper {
    border-radius: 15px;
    border: solid 1px $rfx-light-steel;
    background: white;
    height: 28px;
    display: flex;
    padding: 0 10px;
  }

  &__copy-button {
    flex: 1 1 50%;
    height: 100%;
    border: none;
    outline: none;
    color: $rfx-dark-march;
    white-space: nowrap;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 90px;
    background-color: #fff;
    font-size: 11px;

    &:hover {
      opacity: 0.5;
    }
  }

  &__clear-filters {
    @extend .import__copy-button;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: auto;

    span:first-child {
      font-size: 11px;
    }
  }

  &__grid-select {
    flex-basis: 100px;
  }

  &__title-container {
    padding: 14px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__loader {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 530px);
  }

  &__table {
    position: relative;
    display: flex;
    flex-direction: column;

    &_collapsed {
      .import__row_type {
        padding-left: 0 !important;
      }
    }
  }

  &__update-loader {
    position: absolute;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    background-color: white;
    top: 0;
    opacity: 0.5;
    z-index: 10000;
  }

  &__update-loader-ring {
    margin-top: 200px;
  }

  &__imports {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }

  &__no-data {
    display: flex;
    height: 300px;
    background-color: #fff;
    border-radius: $rfx-default-border-radius;
    align-items: center;
    justify-content: center;
    font-size: 27px;
    font-weight: 600;
    color: $rfx-medium-grey;
    flex: 1 1;
  }

  &__url {
    flex: 1 0 120px;
    color: $rfx-dark-march;
  }

  &__download-button {
    flex: 0 0 45px;

    &.common-table__header-cell {
      flex-basis: 0;
    }
  }

  &__id {
    flex: 0 0 75px;

    &.common-table__header-cell {
      flex-basis: 8.3%;

      &::v-deep .multi-select__title-container {
        padding-left: 70px;
      }
    }
  }

  &__type {
    flex: 0 0 200px;

    &:not(.common-table__header-cell) {
      padding-left: 14px;
      flex: 0 0 186px;
    }
  }

  &__link {
    flex: 1 1;
    padding-left: 15px;
    @include ellipsis;
    text-decoration: none;
    color: $rfx-bright-blue;
    cursor: pointer;
  }

  &__row {
    &_name,
    &_type,
    &_timestamp_processed,
    &_timestamp_failed,
    &_timestamp_completed {
      overflow: hidden;
    }
  }

  &__timestamp_processed,
  &__timestamp_failed,
  &__timestamp_completed {
    flex: 0 0 15.2%;
  }

  &__timestamp_completed {
    &.common-table__header-cell {
      flex: 0 0 17.3%;
    }
  }

  &__button {
    width: 136px;
    padding: 0 20px;
    height: 60px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    border: none;
    outline: none;
    cursor: pointer;
    background-color: $rfx-medium-march;
    color: #fff;
  }

  &__count {
    color: #b2b9be;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    width: 75px;
    white-space: nowrap;
  }
}

.pagination-wrapper {
  display: flex;
  height: 60px;
  margin-top: 10px;
  justify-content: center;
  align-items: center;
  border: 1px solid $rfx-light-steel;
  border-radius: $rfx-default-border-radius;
}

.no-data-block {
  border-bottom: 1px solid $rfx-light-steel;
}

.download-icon {
  cursor: pointer;
  font-size: 20px;
  justify-self: end;
  display: grid;
  color: $rfx-dark-march;
}

</style>
